'use client';

import { useTranslations } from 'next-intl';
import { type CSSProperties, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { BaseFillSubmit } from '@/app/components/icons/BaseFillSubmit';
import { ActivityBannerCarousel } from '@/app/components/shared/ActivityBannerCarousel';
import { FeatureTag } from '@/app/components/shared/FeatureTag';
import { Navs } from '@/app/components/shared/Nav';
import { POSTS_TYPE } from '@/app/components/shared/PostsModal/constants';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { ExploreTabRouterEnum, HomeTabRouterEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { useExploreTabs } from '../_hooks/use-explore-tabs';
import { useGetBannerList } from '../_hooks/use-get-banner-list';
import { resolveHomePostType } from '../_utils';

interface HomeScrollContextValue {
  scrollToTop?: () => void;
}

const HomeScrollContext = createContext<HomeScrollContextValue>({});

export const useHomeScroll = () => {
  return useContext(HomeScrollContext);
};

interface HomeLayoutClientProps {
  children: React.ReactNode;
}

interface HomeQuickEntry {
  key: string;
  title: string;
  path: string;
  image: string;
  badge?: React.ReactNode;
}

const POSTS_TYPE_MAP: Record<string, POSTS_TYPE | undefined> = {
  '/home/recommend': POSTS_TYPE.INSPIRATION,
  '/home/inspiration': POSTS_TYPE.INSPIRATION,
  '/home/short-film': POSTS_TYPE.SHORT_FILM,
  '/home/tutorial': POSTS_TYPE.TUTORIAL,
  '/home/reference': POSTS_TYPE.REFERENCE,
  '/home/activity': undefined,
};

const HOME_QUICK_ENTRY_IMAGE_BASE_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset'
    : 'https://image01.cf.vidu.studio/vidu/media-asset';

const getHomeQuickEntryImage = (assetName: string): string => `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/${assetName}`;

const HOME_QUICK_ENTRY_REFERENCE_IMAGE = getHomeQuickEntryImage('c2v-68811cfd.webp');
const HOME_QUICK_ENTRY_IMG2VIDEO_IMAGE = getHomeQuickEntryImage('i2v-6e715ae0.webp');
const HOME_QUICK_ENTRY_TEMPLATES_IMAGE = getHomeQuickEntryImage('templates-bab5f63b.webp');
const HOME_QUICK_ENTRY_MOTION_CONTROL_IMAGE = getHomeQuickEntryImage('motion_control-e5c1cbf2.webp');
const HOME_QUICK_ENTRY_FACE_SWAP_IMAGE = getHomeQuickEntryImage('face-7de6838a.webp');
const HOME_QUICK_ENTRY_VIDEO_REMAKE_IMAGE = getHomeQuickEntryImage('remake-09a26546.webp');
const HOME_QUICK_ENTRY_VIDEO_EDIT_IMAGE = getHomeQuickEntryImage('videoedit-de4fb509.webp');
const HOME_QUICK_ENTRY_VIDEO_CLIP_EDITOR_IMAGE = getHomeQuickEntryImage('ai-video-editor-801ffe53.webp');
const HOME_QUICK_ENTRY_STORY_GRID_IMAGE = getHomeQuickEntryImage('storyboard-18f8ed1d.webp');
const HOME_QUICK_ENTRY_ONE_CLICK_VIDEO_IMAGE = getHomeQuickEntryImage('oneclick-beafcd9f.webp');
const HOME_QUICK_ENTRY_VIDEO_UPSCALE_IMAGE = getHomeQuickEntryImage('videoupscale-190d7537.webp');
const HOME_QUICK_ENTRY_LIP_SYNC_IMAGE = getHomeQuickEntryImage('lipsync-4eaf1b10.webp');
const HOME_QUICK_ENTRY_TTS_IMAGE = getHomeQuickEntryImage('tts-990e3056.webp');

// 主 banner 比例来自当前设计稿，既用于内容宽高比，也用于桌面端栅格列宽权重。
const HOME_MAIN_BANNER_RATIO = 2.78453;
// 副 banner 比例来自设计稿尺寸：422 / 169，既用于内容宽高比，也用于桌面端栅格列宽权重。
const HOME_SECONDARY_BANNER_RATIO = Math.round((422 / 169) * 1000) / 1000;

const BANNER_ITEM_SECONDARY_STYLE = {
  '--home-banner-aspect-ratio': HOME_SECONDARY_BANNER_RATIO,
  '--home-banner-mobile-aspect-ratio': HOME_MAIN_BANNER_RATIO,
} as CSSProperties;

const BANNER_GRID_TEMPLATE_COLUMNS = `${HOME_MAIN_BANNER_RATIO}fr ${HOME_SECONDARY_BANNER_RATIO}fr ${HOME_SECONDARY_BANNER_RATIO}fr`;
const EAGER_QUICK_ENTRY_IMAGE_COUNT = 3;

const BannerSkeleton = ({ ratio, isMobile }: { ratio: number; isMobile?: boolean }) => (
  <div
    className={`w-full animate-pulse rounded-16 bg-system-white08 [--home-banner-current-aspect-ratio:var(--home-banner-aspect-ratio)] [aspect-ratio:var(--home-banner-current-aspect-ratio)] ${
      isMobile ? 'sm:[--home-banner-current-aspect-ratio:var(--home-banner-mobile-aspect-ratio)]' : ''
    }`}
    style={
      {
        '--home-banner-aspect-ratio': ratio,
        ...(isMobile && { '--home-banner-mobile-aspect-ratio': HOME_MAIN_BANNER_RATIO }),
      } as CSSProperties
    }
  />
);

const getHomeTabPath = (key: HomeTabRouterEnum | ExploreTabRouterEnum) => {
  switch (key) {
    case HomeTabRouterEnum.Recommend:
      return '/home/recommend';
    case ExploreTabRouterEnum.PostReferences:
      return '/home/reference';
    case ExploreTabRouterEnum.ShortFilm:
      return '/home/short-film';
    case ExploreTabRouterEnum.Tutorial:
      return '/home/tutorial';
    case HomeTabRouterEnum.Activity:
      return '/home/activity';
    case ExploreTabRouterEnum.Cases:
      return '/home/cases';
    default:
      return '/home/recommend';
  }
};

export const HomeLayoutClient = ({ children }: HomeLayoutClientProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations();
  const { carouselBanners, fixedBanners, getBannerList, getBannerListLoading } = useGetBannerList();
  const [hasRequestedBannerList, setHasRequestedBannerList] = useState(false);

  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));
  const { open: openPostsModal } = useGlobalModal('postsModal');

  const handlePosts = () => {
    const mappedPostType = POSTS_TYPE_MAP[pathname] || POSTS_TYPE.INSPIRATION;
    const postType = resolveHomePostType(SITE, mappedPostType);
    if (pathname === '/home/activity') {
      openPostsModal({
        hidePostTutorial: true,
        hidePostReference: true,
      });
    } else {
      openPostsModal({
        postType,
      });
    }
    reportEvent('submit_click', {
      submit_position: 'home-recommend-submit',
    });
  };

  const isCarouselLoading = !hasRequestedBannerList || getBannerListLoading;

  useEffect(() => {
    setHasRequestedBannerList(true);
    getBannerList();
  }, [getBannerList]);

  const handleBannerClick = (banner: (typeof fixedBanners)[0], index: number) => {
    reportEvent('fixed_banner_click', {
      banner_id: banner.id || '',
      activity_id: banner.activity_id || '',
      banner_position: index + 1,
      banner_name: banner.title || '',
    });
    if (banner.type === 'activity' && banner.activity_id) {
      router.push(`/activity?id=${banner.activity_id}`);
    } else if (banner.redirect_url) {
      openOrRedirect(banner.redirect_url);
    }
  };

  const scrollToTop = () => {
    const scrollEle = document.getElementById('scrollableDiv');
    if (scrollEle) {
      scrollEle.scrollTop = 0;
    }
  };
  const providersValue = useMemo(() => ({ scrollToTop }), []);
  const homeTabs = useExploreTabs(false);
  const activeTab = useMemo<ExploreTabRouterEnum | HomeTabRouterEnum>(() => {
    const tab = pathname.split('/home/')[1]?.split('/')[0];
    if (
      SITE === 'china' &&
      (tab === ExploreTabRouterEnum.Inspiration ||
        tab === ExploreTabRouterEnum.ShortFilm ||
        tab === ExploreTabRouterEnum.Tutorial)
    ) {
      return HomeTabRouterEnum.Recommend;
    }
    return (tab as ExploreTabRouterEnum | HomeTabRouterEnum) || HomeTabRouterEnum.Recommend;
  }, [pathname]);

  const quickEntries: HomeQuickEntry[] = [
    {
      key: 'viduStream',
      title: t('header.tabs.features.vidu-stream'),
      path: '/vidu-stream',
      image:
        SITE === 'china'
          ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/heart-ee73b3d3.webp'
          : locale === 'zh'
            ? 'https://image01.cf.vidu.studio/vidu/media-asset/heart-ee73b3d3.webp'
            : 'https://image01.cf.vidu.studio/vidu/media-asset/heart_en-d8e43672.webp',
      badge: <FeatureTag />,
    },
    {
      key: 'reference2video',
      title: t('sidebar.toolsItems.referenceToVideo'),
      path: '/create/character2video',
      image: HOME_QUICK_ENTRY_REFERENCE_IMAGE,
    },
    {
      key: 'img2video',
      title: t('sidebar.toolsItems.imageToVideo'),
      path: '/create/img2video',
      image: HOME_QUICK_ENTRY_IMG2VIDEO_IMAGE,
    },
    {
      key: 'templates',
      title: t('sidebar.labItems.templates'),
      path: '/templates',
      image: HOME_QUICK_ENTRY_TEMPLATES_IMAGE,
    },
    {
      key: 'motionControl',
      title: t('tools.motionControl.title'),
      path: '/create/motion-control',
      image: HOME_QUICK_ENTRY_MOTION_CONTROL_IMAGE,
    },
    {
      key: 'storyGrid',
      title: t('tools.storyGrid.title'),
      path: '/create/story-grid',
      image: HOME_QUICK_ENTRY_STORY_GRID_IMAGE,
    },
    {
      key: 'videoClipEditor',
      title: t('videoEditor.list.title'),
      path: '/create/ai-video-cutter',
      image: HOME_QUICK_ENTRY_VIDEO_CLIP_EDITOR_IMAGE,
    },
    {
      key: 'tts',
      title: t('tools.textToSpeech.title'),
      path: '/create/text-to-speech',
      image: HOME_QUICK_ENTRY_TTS_IMAGE,
    },
    {
      key: 'lipSync',
      title: t('tools.lipSync.title'),
      path: '/create/lip-sync',
      image: HOME_QUICK_ENTRY_LIP_SYNC_IMAGE,
    },
    {
      key: 'faceSwap',
      title: t('tools.faceSwap.title'),
      path: '/create/face-swap',
      image: HOME_QUICK_ENTRY_FACE_SWAP_IMAGE,
    },
    {
      key: 'videoRemake',
      title: t('tools.videoRemake.title'),
      path: '/create/video-remake',
      image: HOME_QUICK_ENTRY_VIDEO_REMAKE_IMAGE,
    },
    {
      key: 'oneClickVideoProduction',
      title: t('tools.oneClickVideoProduction.title'),
      path: '/create/one-click-video-production',
      image: HOME_QUICK_ENTRY_ONE_CLICK_VIDEO_IMAGE,
    },
    {
      key: 'videoEdit',
      title: t('task.action.videoEdit'),
      path: '/create/video-edit',
      image: HOME_QUICK_ENTRY_VIDEO_EDIT_IMAGE,
    },
    {
      key: 'upscaleVideo',
      title: t('tools.upscale.video.title'),
      path: '/create/upscale-video',
      image: HOME_QUICK_ENTRY_VIDEO_UPSCALE_IMAGE,
    },
  ];

  return (
    <div className="max-content pt-1">
      <div className="space-y-4">
        {isCarouselLoading ? (
          <div
            className="grid gap-2 [grid-template-columns:var(--home-banner-columns)] sm:[grid-template-columns:1fr]"
            style={{ '--home-banner-columns': BANNER_GRID_TEMPLATE_COLUMNS } as CSSProperties}
          >
            <BannerSkeleton ratio={HOME_MAIN_BANNER_RATIO} />
            <BannerSkeleton ratio={HOME_SECONDARY_BANNER_RATIO} isMobile />
            <BannerSkeleton ratio={HOME_SECONDARY_BANNER_RATIO} isMobile />
          </div>
        ) : (
          <div
            className="grid gap-2 [grid-template-columns:var(--home-banner-columns)] sm:[grid-template-columns:1fr]"
            style={{ '--home-banner-columns': BANNER_GRID_TEMPLATE_COLUMNS } as CSSProperties}
          >
            <div
              className={
                'overflow-hidden rounded-16 border border-system-white12 bg-system-white08 [--home-banner-current-aspect-ratio:var(--home-banner-aspect-ratio)] [aspect-ratio:var(--home-banner-current-aspect-ratio)] sm:[--home-banner-current-aspect-ratio:var(--home-banner-mobile-aspect-ratio)]'
              }
              style={{ '--home-banner-aspect-ratio': HOME_MAIN_BANNER_RATIO } as CSSProperties}
            >
              <ActivityBannerCarousel list={carouselBanners} aspectRatio={HOME_MAIN_BANNER_RATIO} />
            </div>
            {fixedBanners.map((banner, index) => (
              <div
                key={`fixed-${banner.cover_uri}`}
                className={
                  'cursor-pointer overflow-hidden rounded-16 border border-system-white12 bg-system-white08 [--home-banner-current-aspect-ratio:var(--home-banner-aspect-ratio)] [aspect-ratio:var(--home-banner-current-aspect-ratio)] sm:[--home-banner-current-aspect-ratio:var(--home-banner-mobile-aspect-ratio)]'
                }
                style={BANNER_ITEM_SECONDARY_STYLE}
                onClick={() => handleBannerClick(banner, index)}
              >
                <img
                  src={banner.cover_uri}
                  alt={banner.title}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        <div className="-mt-2 flex gap-4 overflow-x-auto pt-2 pb-1">
          {quickEntries.map((entry, index) => (
            <div key={entry.key} className="relative min-w-[220px] flex-[1_0_220px]">
              {entry.badge ? <div className="pointer-events-none absolute top-4 left-4 z-10">{entry.badge}</div> : null}
              <button
                type="button"
                className="group flex w-full items-center justify-between gap-3 overflow-hidden rounded-16 border border-system-white12 bg-system-white08 pl-4 text-left transition-colors hover:bg-system-white12"
                onClick={() => router.push(entry.path)}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-base text-white leading-[1.4]">{entry.title}</div>
                </div>
                <img
                  src={entry.image}
                  alt={entry.title}
                  width={109}
                  height={90}
                  loading={index < EAGER_QUICK_ENTRY_IMAGE_COUNT ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index < EAGER_QUICK_ENTRY_IMAGE_COUNT ? 'high' : 'low'}
                  className="h-[90px] w-[109px] flex-shrink-0 object-cover"
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky top-0 z-20 mt-8 border-system-white08 border-b bg-system-bg08 pb-2">
        <div className="flex items-center justify-between">
          <Navs
            className="h-9 flex-1 pr-14"
            navs={homeTabs}
            active={activeTab}
            getHref={(item) => getHomeTabPath(item.key)}
            onChange={(item) => router.push(getHomeTabPath(item.key))}
          />
          <div className="flex items-center gap-2">
            {userInfo && (
              <>
                <button
                  type="button"
                  className="inline-flex h-9 items-center justify-center rounded-8 border border-system-white12 bg-system-white08 px-5 text-sm transition-colors hover:bg-system-white12 md:hidden"
                  onClick={handlePosts}
                >
                  <BaseFillSubmit className="mr-1.5 text-[18px] text-system-white80" />
                  <span className="font-medium text-white">{t('home.submit')}</span>
                </button>

                {/* 移动端submit 按钮 */}
                <Button
                  variant="outline"
                  size="icon"
                  className="fixed right-4 bottom-4 z-50 hidden h-[56px] w-[56px] items-center justify-center rounded-full bg-ShengshuButton md:flex"
                  onClick={handlePosts}
                >
                  <BaseFillSubmit className="text-[24px] text-black" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <HomeScrollContext.Provider value={providersValue}>{children}</HomeScrollContext.Provider>
    </div>
  );
};
